import React, { useEffect, useMemo, useState } from 'react';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import { formatDateISO, getTodayISO, lunesDeLaSemana, lunesAgenda } from '../../utils/dateUtils';
import AvatarTecnico from '../ui/AvatarTecnico';
import { useBloqueos, estaOcupado } from '../../utils/bloqueos';
import { FRANJAS, franjaDe, franjaDeVisita, etiquetaFranja, pisaFranja } from '../../utils/franjas';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { horarioPasado } from './AvisoHoraPasada';
import FranjaFila, { VisitaAgenda } from './FranjaFila';

export { franjaDe };

// Agenda por franjas (20-oct-2026), la misma en toda la app: arriba el técnico, la semana con lo
// cargado que está cada día, y el día en las 4 franjas (8 a 10, 10 a 12, 12 a 14, 14 a 16) con sus
// visitas, lo que marcó como trabajo propio (rayado) y el hueco para tocar.
//   modo "elegir" (agendar, reprogramar, trabajo nuevo): onHueco({ tecnicoId, franja: "08:00-10:00" })
//   modo "ver" (Panel): con "Todos"; las visitas se abren con onAbrirVisita
const DIAS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const DIA_LARGO = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const ABIERTAS = ['PENDIENTE', 'EN_CAMINO', 'EN_SITIO', 'COMPLETADA'];
const zonaDe = (dir) => String(dir || '').split(',').pop().trim().toLowerCase();

export default function AgendaHuecos({ tecnicos = [], ordenes = [], fecha, onFecha, hueco = null, onHueco, direccion = null,
    excluirId = null, modo = 'elegir', onAbrirVisita = null, onTecnico = null, etiquetaDia = null, cabecera = null, permitirPasado = false }) {
    const elegir = modo === 'elegir';
    const hoy = getTodayISO();
    const [tecSel, setTecSel] = useState(() => hueco?.tecnicoId ?? (tecnicos.length === 1 ? tecnicos[0].id : elegir ? null : 'todos'));
    useEffect(() => { if (tecSel == null && tecnicos.length === 1) setTecSel(tecnicos[0].id); }, [tecnicos, tecSel]);
    useEffect(() => { if (hueco?.tecnicoId != null) setTecSel(hueco.tecnicoId); }, [hueco?.tecnicoId]);
    const todos = tecSel === 'todos';
    const tecnico = tecnicos.find(t => t.id === tecSel) || null;

    // 1-nov-2026: la semana es la del día elegido; un domingo cuenta como el lunes que sigue (antes, un domingo
    // mostraba la semana anterior y el lunes elegido no se veía)
    const offsetDe = (f) => {
        if (!f) return 0;
        const d = new Date(f + 'T12:00:00');
        if (d.getDay() === 0) d.setDate(d.getDate() + 1);
        return Math.round((lunesDeLaSemana(d) - lunesAgenda()) / (7 * 86400000));
    };
    const [offset, setOffset] = useState(() => offsetDe(fecha));
    useEffect(() => { setOffset(offsetDe(fecha)); }, [fecha]); // eslint-disable-line react-hooks/exhaustive-deps
    const semana = useMemo(() => {
        const l = lunesAgenda();
        l.setDate(l.getDate() + offset * 7);
        return DIAS.map((n, i) => { const d = new Date(l); d.setDate(l.getDate() + i); return { n, num: d.getDate(), iso: formatDateISO(d), mes: d.toLocaleDateString('es-AR', { month: 'short' }).replace('.', '') }; });
    }, [offset]);
    // Franja con otra visita del mismo técnico (26-oct-2026): el primer toque avisa, el segundo la suma igual
    const [aviso, setAviso] = useState(null);
    useEffect(() => { setAviso(null); }, [fecha, tecSel]);
    const [versionBloqueos, setVersionBloqueos] = useState(0);
    const bloqueos = useBloqueos(semana[0].iso, semana[semana.length - 1].iso, versionBloqueos);
    // Panel (admin): "Marcos me dijo que de 12 a 14 no puede" → se marca desde la agenda
    const marcar = async (franjaId) => {
        try {
            await api.post('/bloqueos', { tecnicoId: tecnico.id, fecha, franja: franjaId, nota: 'Marcado por el admin' });
            setVersionBloqueos(v => v + 1);
            toast.success(`${tecnico.nombre.split(' ')[0]}: no disponible de ${etiquetaFranja(franjaId)}`);
        } catch (e) { toast.error(e?.response?.data?.mensaje || 'No se pudo marcar'); }
    };
    const quitar = async (ids) => {
        try {
            await Promise.all(ids.map(id => api.delete(`/bloqueos/${id}`)));
            setVersionBloqueos(v => v + 1);
            toast.success('Vuelve a estar disponible');
        } catch { toast.error('No se pudo quitar'); }
    };
    // En el Panel también se ven las que quedaron sin atender
    const estados = elegir ? ABIERTAS : [...ABIERTAS, 'NO_ATENDIDO'];
    const delDia = (iso) => ordenes.filter(o => o.id !== excluirId && estados.includes(o.estado) && String(o.fechaProgramada).slice(0, 10) === iso);
    const esDe = (o, t) => o.tecnicoId === t.id || o.tecnicoNombre === t.nombre;
    const visitasDia = delDia(fecha).filter(o => todos || !tecnico || esDe(o, tecnico))
        .sort((a, b) => String(a.horaEstimada || '').localeCompare(String(b.horaEstimada || '')));
    // Lo ya hecho sin horario va en la franja en que se cerró (28-oct-2026)
    const sinHora = visitasDia.filter(o => !String(o.horaEstimada || '').trim() && !franjaDeVisita(o));
    const fuera = visitasDia.filter(o => String(o.horaEstimada || '').trim() && !franjaDeVisita(o));

    // Domingo no está en la grilla lun–sáb: se pasa al lunes
    useEffect(() => {
        if (fecha && new Date(fecha + 'T12:00:00').getDay() === 0) {
            const d = new Date(fecha + 'T12:00:00'); d.setDate(d.getDate() + 1);
            onFecha(formatDateISO(d));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fecha]);
    const diaHoy = (() => { const d = new Date(); if (d.getDay() === 0) d.setDate(d.getDate() + 1); return formatDateISO(d); })();
    const enHoy = offset === 0 && fecha === diaHoy;

    // Carga del día para el técnico elegido: una rayita por franja (visita / no disponible / libre)
    const carga = (iso) => FRANJAS.map(f => {
        if (!tecnico) return null;
        if (estaOcupado(bloqueos, tecnico.id, iso, f.id)) return 'ocupado';
        return delDia(iso).some(o => esDe(o, tecnico) && franjaDeVisita(o) === f.id) ? 'visita' : 'libre';
    });
    const elegirTecnico = (t) => { setTecSel(t === 'todos' ? 'todos' : t.id); if (t !== 'todos' && onTecnico) onTecnico(t); };
    const fd = new Date((fecha || hoy) + 'T12:00:00');
    const pasadaDia = (fecha || hoy) < hoy;

    return (
        <div className="space-y-3">
            {(tecnicos.length > 1 || !elegir) && (
                <div className="flex gap-1.5 overflow-x-auto sin-barra -mx-1 px-1">
                    {!elegir && (
                        <button type="button" onClick={() => elegirTecnico('todos')} aria-pressed={todos}
                            className={`shrink-0 h-10 px-4 rounded-full text-body font-black active:scale-95 ${todos ? 'bg-ink text-page' : 'bg-chip text-secondary'}`}>Todos</button>
                    )}
                    {tecnicos.map(t => (
                        <button key={t.id} type="button" onClick={() => elegirTecnico(t)} aria-pressed={tecSel === t.id}
                            className={`shrink-0 h-10 pl-1.5 pr-3.5 rounded-full inline-flex items-center gap-2 text-body font-black active:scale-95 ${tecSel === t.id ? 'bg-ink text-page' : 'bg-chip text-secondary'}`}>
                            <AvatarTecnico nombre={t.nombre} size={28} />{t.nombre.split(' ')[0]}
                        </button>
                    ))}
                </div>
            )}

            <div className="flex items-center gap-1.5">
                <button type="button" onClick={() => setOffset(o => o - 1)} aria-label="Semana anterior" className="w-8 h-14 shrink-0 rounded-lg flex items-center justify-center text-muted active:bg-chip"><LuChevronLeft size={16} /></button>
                <div className="flex-1 grid grid-cols-6 gap-1.5">
                    {semana.map(d => {
                        const sel = d.iso === fecha;
                        const c = carga(d.iso);
                        const vs = delDia(d.iso);
                        return (
                            <button key={d.iso} type="button" disabled={elegir && !permitirPasado && d.iso < hoy} onClick={() => onFecha(d.iso)}
                                className={`${etiquetaDia ? 'h-16' : 'h-14'} rounded-xl flex flex-col items-center justify-center gap-1 text-label font-black active:scale-95 disabled:opacity-30 ${sel ? 'bg-[#C9341F] text-white' : 'bg-chip text-muted'} ${d.iso === hoy && !sel ? 'ring-2 ring-[#C9341F]/50' : ''}`}>
                                <span className="leading-none">{d.iso === hoy ? 'Hoy' : d.n}</span>
                                <span className={`text-body leading-none ${sel ? 'text-white' : 'text-ink'}`}>{d.num}</span>
                                {tecnico ? (
                                    <span className="flex gap-0.5">
                                        {c.map((x, i) => <span key={i} className={`w-1.5 h-1 rounded-sm ${x === 'visita' ? (sel ? 'bg-white' : 'bg-[#D9932A]') : x === 'ocupado' ? (sel ? 'bg-white/50' : 'bg-[#78716C]') : (sel ? 'bg-white/25' : 'bg-black/15 dark:bg-white/15')}`} />)}
                                    </span>
                                ) : etiquetaDia ? (
                                    // Agenda de Trabajos (27-oct-2026): la zona del día debajo del número
                                    <span className={`text-[10px] leading-none font-black ${sel ? 'text-white' : 'text-muted'}`}>{etiquetaDia(d.iso)}</span>
                                ) : (
                                    <span className="h-1 text-[9px] leading-none">{vs.length ? vs.length : ''}</span>
                                )}
                            </button>
                        );
                    })}
                </div>
                <button type="button" onClick={() => setOffset(o => o + 1)} aria-label="Semana siguiente" className="w-8 h-14 shrink-0 rounded-lg flex items-center justify-center text-muted active:bg-chip"><LuChevronRight size={16} /></button>
            </div>

            {cabecera}
            <div className={`flex items-center justify-between gap-2 ${cabecera ? 'hidden' : ''}`}>
                <p className="min-w-0">
                    <span className="block text-body-lg font-black text-ink">{DIA_LARGO[fd.getDay()]} {fd.getDate()}{todos ? '' : tecnico ? ` · ${tecnico.nombre.split(' ')[0]}` : ''}</span>
                    {/* 27-oct-2026: cómo va el día de un vistazo */}
                    {!elegir && visitasDia.length > 0 && (() => {
                        const hechas = visitasDia.filter(o => o.estado === 'COMPLETADA').length;
                        const falta = visitasDia.length - hechas;
                        return <span className="block text-caption text-muted">{[`${visitasDia.length} visita${visitasDia.length !== 1 ? 's' : ''}`,
                            hechas ? `${hechas} hecha${hechas !== 1 ? 's' : ''}` : null, falta ? `${falta} por hacer` : 'todo hecho'].filter(Boolean).join(' · ')}</span>;
                    })()}
                </p>
                {!enHoy && (
                    <button type="button" onClick={() => { setOffset(0); onFecha(diaHoy); }}
                        className="h-8 px-3 rounded-lg border border-brand-red text-label font-black text-ink active:scale-95">Hoy</button>
                )}
            </div>

            {/* 28-oct-2026: con un técnico elegido, lo de los otros no se ve: se avisa, así no parece que no está */}
            {!elegir && tecnico && (() => {
                const otros = delDia(fecha).filter(o => !esDe(o, tecnico));
                if (!otros.length) return null;
                const nombres = [...new Set(otros.map(o => String(o.tecnicoNombre || 'Otro').split(' ')[0]))];
                return (
                    <button type="button" onClick={() => elegirTecnico('todos')}
                        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl bg-panel text-left active:scale-[0.99]">
                        <span className="text-caption text-secondary">{nombres.join(' y ')} {nombres.length > 1 ? 'tienen' : 'tiene'} {otros.length} visita{otros.length !== 1 ? 's' : ''} este día</span>
                        <span className="text-caption font-black text-ink underline underline-offset-2 shrink-0">Ver todos</span>
                    </button>
                );
            })()}
            {elegir && !tecnico ? (
                <p className="py-3 text-caption text-muted">Elegí el técnico para ver sus horarios.</p>
            ) : (
                <div className="space-y-2">
                    {FRANJAS.map(f => {
                        const vs = visitasDia.filter(o => franjaDeVisita(o) === f.id);
                        const ocupados = (todos ? tecnicos : tecnico ? [tecnico] : []).filter(t => estaOcupado(bloqueos, t.id, fecha, f.id));
                        const libres = todos ? tecnicos.filter(t => !ocupados.includes(t) && !vs.some(o => esDe(o, t))).map(t => t.nombre) : [];
                        const zona = elegir && direccion && delDia(fecha).some(o => tecnico && esDe(o, tecnico) && zonaDe(o.direccion) && zonaDe(o.direccion) === zonaDe(direccion));
                        return (
                            <FranjaFila key={f.id} franja={f} visitas={vs} tecnico={tecnico} todos={todos}
                                ocupados={ocupados.map(t => t.nombre)} libres={libres} elegir={elegir}
                                on={hueco?.tecnicoId === tecnico?.id && hueco?.franja === f.id}
                                pasada={!permitirPasado && !!horarioPasado(fecha, f.id)} zona={zona}
                                aviso={aviso === f.id}
                                onElegir={() => {
                                    if (vs.length && aviso !== f.id && !(hueco?.tecnicoId === tecnico?.id && hueco?.franja === f.id)) { setAviso(f.id); return; }
                                    setAviso(null);
                                    onHueco && onHueco({ tecnicoId: tecnico.id, franja: f.id });
                                }}
                                onMarcar={!elegir && tecnico && !pasadaDia ? () => marcar(f.id) : null}
                                onQuitar={!elegir && tecnico ? (() => {
                                    const ids = bloqueos.filter(b => b.tecnicoId === tecnico.id && b.fecha === fecha && !b.semanal && b.id
                                        && (b.franja === f.id || b.franja === 'DIA' || pisaFranja(b.franja, f.id))).map(b => b.id);
                                    return ids.length ? () => quitar(ids) : null;
                                })() : null}
                                onAbrir={onAbrirVisita} />
                        );
                    })}
                    {[['Fuera de horario', fuera], ['Sin hora', sinHora]].filter(([, l]) => l.length).map(([titulo, l]) => (
                        <div key={titulo} className="flex gap-2.5">
                            <p className="w-14 shrink-0 pt-2 text-body font-black text-muted leading-tight">{titulo}</p>
                            <div className="flex-1 min-w-0 space-y-1.5">
                                {l.map(o => <VisitaAgenda key={o.id} o={o} conAvatar={todos} onAbrir={onAbrirVisita} />)}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
